import { useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Cross2Icon } from '@radix-ui/react-icons'
import { cn } from '@/lib/utils'

interface RightSideBarInfoProps {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: React.ReactNode
  className?: string
}

export default function RightSideBarInfo({
  open,
  onClose,
  title,
  description,
  children,
  className,
}: RightSideBarInfoProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, handleClose])

  // Close on click outside (on the overlay)
  const handleOverlayClick = useCallback(() => {
    handleClose()
  }, [handleClose])

  return createPortal(
    <>
      {/* Overlay — semi-transparent over entire page */}
      <div
        className={cn(
          'fixed inset-0 z-[9999] bg-black/20 transition-opacity duration-300',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="complementary"
        aria-label={title}
        aria-hidden={!open}
        className={cn(
          'fixed right-4 top-4 bottom-4 w-[420px]',
          'bg-white',
          'rounded-2xl shadow-2xl border border-gray-200',
          'flex flex-col z-[10000]',
          'transition-all duration-300 ease-in-out',
          open
            ? 'translate-x-0 opacity-100'
            : 'translate-x-[calc(100%+1rem)] opacity-0 pointer-events-none',
          className
        )}
      >
        {/* Header */}
        <div className="flex flex-col space-y-1 px-5 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-md p-1 text-gray-400 transition-colors hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-label="Cerrar panel"
              tabIndex={open ? 0 : -1}
            >
              <Cross2Icon className="h-4 w-4" />
            </button>
          </div>
          {description && <p className="text-sm text-gray-500">{description}</p>}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {children}
        </div>
      </div>
    </>,
    document.body
  )
}
